import styled, { css } from 'styled-components';
import colors from '@honeybook/hbui/dist/styles/colors';
import is, { isNot, isOr } from 'styled-is';
import { flexCenterAll, hideInPrint } from '@honeybook/hbui/dist/styles/mixins';
import { transition } from '@honeybook/hbui/dist/styles/animations';
import { RightFloatingCogIcon } from '~/common/IconWrappers/FloatingCogIconWrapper.styles';
import { AIChatIcon } from '~/common/IconWrappers/AIChatIconWrapper.styles';
import { ImageBlock } from '~/models/blocks/image-block.model';
import {
    Alignment,
    IconColor,
    IImageBlockStyles
} from '~/types/block-styles.types';
import { upToMedium } from '@honeybook/hbui/dist/styles/breakpoints';
import { TBlock } from '~/types/builder.types';
import { IFlowTheme } from '~/types/model.types';
import {
    AppearanceActionsWrapper,
    ServicesContainer
} from '~/components/common/InvoiceBlock/InvoiceBlock.styles';

export function getAlignmentCSSString(alignment: Alignment) {
    let justify;
    switch (alignment) {
        case Alignment.LEFT:
            justify = 'flex-start';
            break;
        case Alignment.CENTER:
            justify = 'center';
            break;
        case Alignment.RIGHT:
            justify = 'flex-end';
            break;
        default:
            return null;
    }

    return css`
        justify-content: ${justify};
    `;
}

export function getTextAlignCSSString(alignment: Alignment) {
    let justify;
    switch (alignment) {
        case Alignment.LEFT:
            justify = 'left';
            break;
        case Alignment.CENTER:
            justify = 'center';
            break;
        case Alignment.RIGHT:
            justify = 'right';
            break;
        default:
            return null;
    }

    return css`
        display: block;
        text-align: ${justify};
    `;
}

export function getFillColorCSSString(icon_color: string, theme: IFlowTheme) {
    let color;
    switch (icon_color) {
        case IconColor.THEME:
            color = theme.styles.color;
            break;
        case IconColor.WHITE:
            color = '#FFFFFF';
            break;
        default:
            color = null;
    }

    return color;
}

export function getAlignment(alignment: Alignment) {
    let justify;
    switch (alignment) {
        case Alignment.LEFT:
            justify = 'flex-start';
            break;
        case Alignment.CENTER:
            justify = 'center';
            break;
        case Alignment.RIGHT:
            justify = 'flex-end';
            break;
        default:
            return null;
    }

    return justify;
}

function getImageBlockAlignment(block: TBlock) {
    if (block instanceof ImageBlock && block.image) {
        const flexAlignment = getAlignment(
            (block.styles as IImageBlockStyles).alignment
        );

        return `align-self: ${flexAlignment};`;
    }
    return null;
}

interface BlockWrapperProps {
    selected: boolean;
    isSelectable: boolean;
    isDimmed?: boolean;
    isHoveredExact?: boolean;
    isHoveredIndirect?: boolean;
    isRootOfTextBlockSelected?: boolean;
    block?: TBlock;
    isChildSelected?: boolean;
    isMockHoverState?: boolean;
    shouldExpandOnSelect?: boolean;
    shouldExpandTop?: boolean;
    paddingSize?: number;
    hasZIndex?: boolean;
    isDragging?: boolean;
    shouldHighlightBlock?: boolean;
}

export const HideForPrint = styled.div`
    ${hideInPrint}
`;

export const FloatingContainer = styled.div`
    position: absolute;
    height: 0;
    left: 0;
    top: 0;
    min-width: 100%;
    padding: 0 4px;
    display: flex;
    justify-content: space-between;
    > :first-child {
        margin-right: 10px;
    }
`;

export const FakeStyledBlockWrapper = styled.div<{
    selected: boolean;
    showFaintLine?: boolean;
}>`
    position: relative;
    border: 1px solid transparent;

    ${isNot('selected')`
        &:hover {
            border: 1px solid ${colors.blue20_40};
            cursor: pointer;
            opacity: 1;
        }
    `}

    ${is('showFaintLine')`
            border: 1px solid ${colors.blue20_20};
    `}
    
    ${is('selected')`
        border: 1px solid ${colors.blue20};
    `}
`;

const BlockWrapperStyles = css<BlockWrapperProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100vw;
    border: 1px solid transparent;
    ${transition()}

    ${is('isHoveredExact')`
        border: 1px solid ${colors.blue20_40};
    `} 
    
    ${is('isRootOfTextBlockSelected')`
        margin: 10px 0;
    `}

    ${isOr('isHoveredIndirect', 'isChildSelected')`
        border: 1px dashed ${colors.blue20_40};
    `}
    
    ${is('shouldExpandOnSelect')`
        padding: ${({ paddingSize }) => paddingSize ?? 20}px 0;
    `} 
    
    ${is('shouldExpandTop')`
        padding-top: 70px;
    `}

    ${is('selected')`
        z-index: 1;
        border: ${({ shouldHighlightBlock }) =>
            shouldHighlightBlock ? 8 : 1}px solid ${colors.blue20};
        cursor: initial;
        box-shadow: ${({ block }) =>
            block?.type === 'container_block'
                ? '0 10px 30px 0 rgba(0, 0, 0, 0.1)'
                : ''};
    `}

    ${is('isChildSelected')`
        box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.1);
    `}

    ${isNot('selected')`
        cursor: pointer;
    `}

    ${is('isDimmed')`
        opacity: 0.4;
        &:hover {
        opacity: 0.8;
    `}

    ${isNot('isSelectable')`
        cursor: initial;
        border: 1px solid transparent;
    `}

    ${isOr(
        'isHoveredExact',
        'isHoveredIndirect',
        'selected',
        'isChildSelected',
        'isMockHoverState'
    )`
        ${RightFloatingCogIcon} {
            display: block;
        }
        ${AIChatIcon} {
            display: block;
        }
        ${AppearanceActionsWrapper} {
            display: flex;
        }
        ${ServicesContainer} {
            ${upToMedium} {
                padding-top: 0;
             }
        }
    `}

    ${is('hasZIndex')`
        z-index: 1;
    `}
    
    ${is('isDragging')`
        opacity: 0.5;
    `}

    ${props => getImageBlockAlignment(props.block)}
`;

export const StyledBlockWrapper = styled.div<BlockWrapperProps>`
    ${BlockWrapperStyles}
`;

export const StyledClickableBlockWrapper = styled.div<BlockWrapperProps>`
    ${BlockWrapperStyles}
`;

export const DnDWrapper = styled.div<{ ref: any }>``;

export const DnDDropPlaceholder = styled.div`
    ${flexCenterAll};
    height: 180px;
    width: 100%;
    border: dashed 1px ${colors.blue10};
    background-color: ${colors.blue20_08};
`;

export const OptionFloatingContainer = styled(FloatingContainer)`
    min-width: 90%;
`;
